import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, Loader2, Wand2, Upload, Image as ImageIcon, X, FileEdit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import NavigationMenu from '../components/NavigationMenu';
import UsageLimitBanner from '../components/UsageLimitBanner';
import CancelledSubscriptionBanner from '../components/CancelledSubscriptionBanner';
import LimitReachedModal from '../components/LimitReachedModal';
import { useSubscription } from '../hooks/useSubscription';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIGeneratorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canUseAI, canCreateRecipe, refresh } = useSubscription();
  const [dietTypes, setDietTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitType, setLimitType] = useState('ai'); // 'ai' or 'recipe'
  
  // Por Nome
  const [recipeName, setRecipeName] = useState('');
  const [desiredTime, setDesiredTime] = useState('');
  const [difficulty, setDifficulty] = useState('any');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [servingType, setServingType] = useState('portions'); // 'portions' or 'weight'
  const [portionValue, setPortionValue] = useState('');
  
  // Por Ingredientes
  const [ingredients, setIngredients] = useState(['']);
  const [preferences, setPreferences] = useState('');
  const [dietType, setDietType] = useState('');
  
  // Por Imagem
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Comum
  const [servings, setServings] = useState(4);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  useEffect(() => {
    fetchDietTypes();
  }, []);

  const fetchDietTypes = async () => {
    try {
      const response = await axios.get(`${API}/diet-types`);
      setDietTypes(response.data.diet_types);
    } catch (error) {
      console.error('Error fetching diet types:', error);
    }
  };

  const addIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const removeIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const handleIngredientChange = (index, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  // Gerar por Nome
  const handleGenerateByName = async () => {
    if (!recipeName.trim()) {
      toast.error(t('aiGenerator.errorGenerating'));
      return;
    }

    // CHECK AI LIMIT
    if (!canUseAI) {
      setLimitType('ai');
      setShowLimitModal(true);
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      
      setProgressMessage('Buscando receita...');
      setProgress(20);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressMessage('IA criando receita...');
      setProgress(50);
      
      // Build comprehensive preferences with ALL user inputs
      let fullPreferences = `Criar receita completa para: ${recipeName}`;
      
      // Add serving size/weight specification with proper units
      if (portionValue) {
        const measurementText = {
          'portions': `${portionValue} porções`,
          'g': `${portionValue}g (${portionValue} gramas)`,
          'kg': `${portionValue}kg (${portionValue} quilogramas)`,
          'ml': `${portionValue}ml (${portionValue} mililitros)`,
          'l': `${portionValue}L (${portionValue} litros)`
        };
        fullPreferences += `. Quantidade: ${measurementText[servingType] || `${portionValue} ${servingType}`}`;
      }
      
      // Add desired time
      if (desiredTime) {
        fullPreferences += `. Tempo de preparo máximo: ${desiredTime} minutos`;
      }
      
      // Add difficulty level
      if (difficulty && difficulty !== 'any') {
        const difficultyText = {
          'facil': 'fácil',
          'media': 'média',
          'dificil': 'difícil'
        };
        fullPreferences += `. Nível de dificuldade: ${difficultyText[difficulty] || difficulty}`;
      }
      
      // Add diet type
      if (dietType) {
        fullPreferences += `. Tipo de dieta: ${dietType}`;
      }
      
      // Add any additional custom information
      if (additionalInfo.trim()) {
        fullPreferences += `. Requisitos adicionais: ${additionalInfo}`;
      }
      
      console.log('📋 Prompt COMPLETO enviado para IA:', fullPreferences);
      
      const response = await axios.post(`${API}/ai/generate-recipe`, {
        ingredients: [],
        preferences: fullPreferences,
        diet_type: null, // Already included in preferences
        servings: servingType === 'portions' ? (parseInt(portionValue) || 4) : 4
      }, { timeout: 90000 });
      
      setProgressMessage('Gerando imagem...');
      setProgress(90);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProgressMessage('Pronto!');
      setProgress(100);
      
      setGeneratedRecipe(response.data);
      await refresh(); // Refresh usage stats after AI generation
      toast.success(t('aiGenerator.recipeGenerated'));
    } catch (error) {
      console.error('Error generating recipe:', error);
      toast.error(t('aiGenerator.errorGenerating'));
      setProgress(0);
      setProgressMessage('');
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
        setProgressMessage('');
      }, 1000);
    }
  };

  // Gerar por Ingredientes
  const handleGenerateByIngredients = async () => {
    const validIngredients = ingredients.filter(ing => ing.trim());
    
    if (validIngredients.length === 0) {
      toast.error(t('aiGenerator.errorGenerating'));
      return;
    }

    // CHECK AI LIMIT
    if (!canUseAI) {
      setLimitType('ai');
      setShowLimitModal(true);
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      
      setProgressMessage('Preparando ingredientes...');
      setProgress(20);
      
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setProgressMessage('IA criando receita...');
      setProgress(50);
      
      const response = await axios.post(`${API}/ai/generate-recipe`, {
        ingredients: validIngredients,
        preferences: preferences || null,
        diet_type: dietType || null,
        servings
      }, { timeout: 90000 });
      
      setProgressMessage('Gerando imagem...');
      setProgress(90);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProgressMessage('Pronto!');
      setProgress(100);
      
      setGeneratedRecipe(response.data);
      await refresh(); // Refresh usage stats after AI generation
      toast.success(t('aiGenerator.recipeGenerated'));
    } catch (error) {
      console.error('Error generating recipe:', error);
      toast.error(t('aiGenerator.errorGenerating'));
      setProgress(0);
      setProgressMessage('');
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
        setProgressMessage('');
      }, 1000);
    }
  };

  // Upload de Imagem
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('common.error'));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGenerateFromImage = async () => {
    if (!uploadedImage) {
      toast.error(t('aiGenerator.errorGenerating'));
      return;
    }

    // CHECK AI LIMIT
    if (!canUseAI) {
      setLimitType('ai');
      setShowLimitModal(true);
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      
      setProgressMessage('Preparando imagem...');
      setProgress(15);
      
      const blob = await fetch(uploadedImage).then(r => r.blob());
      const formData = new FormData();
      formData.append('image', blob, 'dish.jpg');

      setProgressMessage('IA analisando imagem...');
      setProgress(35);

      const recognizeResponse = await axios.post(`${API}/ai/recognize-dish`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });

      const dishInfo = recognizeResponse.data;
      
      setProgressMessage(`Prato: ${dishInfo.dish_name}`);
      setProgress(55);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setProgressMessage('Gerando receita...');
      setProgress(70);
      
      const generateResponse = await axios.post(`${API}/ai/generate-recipe`, {
        ingredients: dishInfo.key_ingredients || [],
        preferences: `Criar receita para: ${dishInfo.dish_name}. ${dishInfo.description}`,
        diet_type: null,
        servings
      }, { timeout: 90000 });
      
      setProgressMessage('Criando imagem...');
      setProgress(90);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setProgressMessage('Pronto!');
      setProgress(100);
      
      setGeneratedRecipe(generateResponse.data);
      await refresh(); // Refresh usage stats after AI generation
      toast.success(t('aiGenerator.recipeGenerated'));
    } catch (error) {
      console.error('Error generating from image:', error);
      toast.error(t('aiGenerator.errorGenerating'));
      setProgress(0);
      setProgressMessage('');
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
        setProgressMessage('');
      }, 1000);
    }
  };

  const handleSaveRecipe = async () => {
    if (!generatedRecipe) return;

    // CHECK RECIPE LIMIT
    if (!canCreateRecipe) {
      setLimitType('recipe');
      setShowLimitModal(true);
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(`${API}/recipes`, generatedRecipe);
      toast.success(t('recipe.recipeSaved'));
      await refresh(); // Refresh usage stats
      navigate(`/recipe/${response.data.id}`);
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error(error.response?.data?.detail || t('recipe.errorSavingRecipe'));
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setGeneratedRecipe(null);
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleRecipeChange = (field, value) => {
    setGeneratedRecipe(prev => ({ ...prev, [field]: value }));
  };

  const handleIngredientEdit = (index, field, value) => {
    const newIngredients = [...generatedRecipe.ingredients];
    newIngredients[index][field] = value;
    setGeneratedRecipe(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const handleInstructionEdit = (index, value) => {
    const newInstructions = [...generatedRecipe.instructions];
    newInstructions[index] = value;
    setGeneratedRecipe(prev => ({ ...prev, instructions: newInstructions }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/30 via-white to-orange-50/30">
      <NavigationMenu />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Cancelled Subscription Banner */}
        <CancelledSubscriptionBanner className="mb-4" />
        
        {/* Usage Limit Banner */}
        <UsageLimitBanner className="mb-8" />
        
        <div className="text-center mb-8">
          <div className="flex justify-center items-center gap-2 mb-3">
            <Sparkles className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-4xl font-bold text-amber-900 mb-2">{t('aiGenerator.title')}</h1>
          <p className="text-gray-600">{t('aiGenerator.selectMethodDescription')}</p>
        </div>

        {/* Progress Bar - Sutil */}
        {loading && progress > 0 && (
          <Card className="mb-6 p-4 border border-orange-200/50 bg-white/80 backdrop-blur-sm shadow-sm">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{progressMessage}</span>
                <span className="text-sm font-semibold text-orange-600">{progress}%</span>
              </div>
              
              <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-orange-400 to-amber-400 transition-all duration-700 ease-out rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </Card>
        )}

        {/* Tabs para 3 métodos */}
        <Tabs defaultValue="name" className="w-full">
          <TabsList className="ai-generator-tabs grid w-full grid-cols-3 mb-6 bg-white/50 backdrop-blur-sm border border-gray-200">
            <TabsTrigger value="name" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900">
              <FileEdit className="w-4 h-4 mr-2" />
              {t('aiGenerator.byName')}
            </TabsTrigger>
            <TabsTrigger value="ingredients" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900">
              <Plus className="w-4 h-4 mr-2" />
              {t('aiGenerator.byIngredients')}
            </TabsTrigger>
            <TabsTrigger value="image" className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-900">
              <ImageIcon className="w-4 h-4 mr-2" />
              {t('aiGenerator.byImage')}
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Por Nome */}
          <TabsContent value="name">
            <Card className="p-6 border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.recipeName')}</Label>
                  <Input
                    data-testid="recipe-name-input"
                    value={recipeName}
                    onChange={(e) => setRecipeName(e.target.value)}
                    placeholder={t('aiGenerator.recipeNamePlaceholder')}
                    className="mt-2 border-gray-300 focus:border-orange-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.dietType')}</Label>
                    <Select value={dietType || "none"} onValueChange={(v) => setDietType(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-2 border-gray-300">
                        <SelectValue placeholder={t('common.none')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('common.none')}</SelectItem>
                        {dietTypes.map((diet) => (
                          <SelectItem key={diet} value={diet}>{diet}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.prepTime')} ({t('common.minutes')})</Label>
                    <Input
                      type="number"
                      min="5"
                      value={desiredTime}
                      onChange={(e) => setDesiredTime(e.target.value)}
                      placeholder="Ex: 30"
                      className="mt-2 border-gray-300"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.quantity')}</Label>
                  <div className="flex gap-2 mt-2 quantity-input-container">
                    <Select value={servingType} onValueChange={setServingType}>
                      <SelectTrigger className="w-28 sm:w-32 border-gray-300 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portions">{t('aiGenerator.portions')}</SelectItem>
                        <SelectItem value="g">{t('aiGenerator.grams')}</SelectItem>
                        <SelectItem value="kg">{t('aiGenerator.kg')}</SelectItem>
                        <SelectItem value="ml">{t('aiGenerator.ml')}</SelectItem>
                        <SelectItem value="l">{t('aiGenerator.liters')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      value={portionValue}
                      onChange={(e) => setPortionValue(e.target.value)}
                      placeholder={servingType === 'portions' ? 'Ex: 4' : 'Ex: 500'}
                      className="flex-1 border-gray-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.difficulty')}</Label>
                    <Select value={difficulty} onValueChange={setDifficulty}>
                      <SelectTrigger className="mt-2 border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('aiGenerator.any')}</SelectItem>
                        <SelectItem value="facil">{t('aiGenerator.easy')}</SelectItem>
                        <SelectItem value="media">{t('aiGenerator.medium')}</SelectItem>
                        <SelectItem value="dificil">{t('aiGenerator.hard')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.customNotes')}</Label>
                  <Textarea
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder={t('aiGenerator.customNotesPlaceholder')}
                    className="mt-2 border-gray-300 min-h-20"
                  />
                </div>

                <Button
                  data-testid="generate-by-name-button"
                  onClick={handleGenerateByName}
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.generating')}</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />{t('aiGenerator.generateRecipe')}</>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Tab 2: Por Ingredientes */}
          <TabsContent value="ingredients">
            <Card className="p-6 border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
              <div className="space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.availableIngredients')}</Label>
                    <Button 
                      onClick={addIngredient}
                      variant="ghost"
                      size="sm"
                      className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t('common.add')}
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {ingredients.map((ingredient, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          data-testid={`ingredient-${index}`}
                          type="text"
                          placeholder={t('aiGenerator.ingredientsPlaceholder')}
                          value={ingredient}
                          onChange={(e) => handleIngredientChange(index, e.target.value)}
                          className="flex-1 border-gray-300"
                        />
                        {ingredients.length > 1 && (
                          <Button
                            onClick={() => removeIngredient(index)}
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.preferences')}</Label>
                  <Textarea
                    value={preferences}
                    onChange={(e) => setPreferences(e.target.value)}
                    placeholder={t('aiGenerator.preferencesPlaceholder')}
                    className="mt-2 border-gray-300 min-h-20"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('aiGenerator.dietType')}</Label>
                    <Select value={dietType || "none"} onValueChange={(v) => setDietType(v === "none" ? "" : v)}>
                      <SelectTrigger className="mt-2 border-gray-300">
                        <SelectValue placeholder={t('common.none')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t('common.none')}</SelectItem>
                        {dietTypes.map((diet) => (
                          <SelectItem key={diet} value={diet}>{diet}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('recipe.servings')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={servings}
                      onChange={(e) => setServings(parseInt(e.target.value) || 1)}
                      className="mt-2 border-gray-300"
                    />
                  </div>
                </div>

                <Button
                  data-testid="generate-by-ingredients-button"
                  onClick={handleGenerateByIngredients}
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.generating')}</>
                  ) : (
                    <><Wand2 className="w-4 h-4 mr-2" />{t('aiGenerator.generateRecipe')}</>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Tab 3: Por Imagem */}
          <TabsContent value="image">
            <Card className="p-6 border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm">
              <div className="space-y-5">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    {t('aiGenerator.uploadImage')}
                  </Label>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />

                  {!uploadedImage ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/30 transition-all"
                    >
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">{t('aiGenerator.clickToUpload')}</p>
                      <p className="text-sm text-gray-400 mt-1">{t('aiGenerator.orDragImage')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative">
                        <img
                          src={uploadedImage}
                          alt="Uploaded"
                          className="w-full h-56 object-cover rounded-lg"
                        />
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-700 rounded-full p-2 shadow-sm transition-all"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {uploadedImage && (
                  <div>
                    <Label className="text-sm font-medium text-gray-700">{t('recipe.servings')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={servings}
                      onChange={(e) => setServings(parseInt(e.target.value) || 1)}
                      className="mt-2 border-gray-300"
                    />
                  </div>
                )}

                <Button
                  data-testid="generate-from-image-button"
                  onClick={handleGenerateFromImage}
                  disabled={loading || !uploadedImage}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('aiGenerator.analyzing')}</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" />{t('aiGenerator.generateFromImage')}</>
                  )}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Recipe Preview */}
        {generatedRecipe && (
          <Card className="mt-6 p-6 border border-green-200 bg-white/80 backdrop-blur-sm shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Receita Gerada</h2>
              {!isEditing && (
                <div className="flex flex-col sm:flex-row gap-2 generated-recipe-actions">
                  <Button
                    onClick={handleEdit}
                    variant="outline"
                    size="sm"
                    className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto"
                  >
                    Editar
                  </Button>
                  <Button
                    onClick={handleDiscard}
                    variant="outline"
                    size="sm"
                    className="border-red-200 text-red-600 hover:bg-red-50 w-full sm:w-auto"
                  >
                    Descartar
                  </Button>
                  <Button
                    data-testid="save-generated-button"
                    onClick={handleSaveRecipe}
                    disabled={saving}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar
                  </Button>
                </div>
              )}
              {isEditing && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => setIsEditing(false)} variant="outline" size="sm" className="w-full sm:w-auto">Cancelar</Button>
                  <Button
                    onClick={handleSaveRecipe}
                    disabled={saving}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                {generatedRecipe.image_url && (
                  <img 
                    src={generatedRecipe.image_url} 
                    alt={generatedRecipe.title}
                    className="w-full rounded-lg shadow-sm"
                  />
                )}
              </div>

              <div className="space-y-4">
                {isEditing ? (
                  <>
                    <Input
                      value={generatedRecipe.title}
                      onChange={(e) => handleRecipeChange('title', e.target.value)}
                      className="text-xl font-semibold"
                    />
                    <Textarea
                      value={generatedRecipe.description}
                      onChange={(e) => handleRecipeChange('description', e.target.value)}
                      className="min-h-20"
                    />
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl font-semibold text-gray-900">{generatedRecipe.title}</h3>
                    <p className="text-gray-600">{generatedRecipe.description}</p>
                  </>
                )}

                <div className="flex gap-3 text-sm">
                  <Badge variant="secondary">{generatedRecipe.category}</Badge>
                  <span className="text-gray-600">{generatedRecipe.servings} porções</span>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Ingredientes:</h4>
                  <ul className="space-y-1 text-sm">
                    {generatedRecipe.ingredients.map((ing, index) => (
                      <li key={index} className="text-gray-700">
                        • {ing.quantity} {ing.unit} de {ing.name}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Informações Nutricionais */}
                {generatedRecipe.nutrition?.per_serving && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                    <h4 className="font-semibold text-green-900 mb-3">Informações Nutricionais (por porção)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-700">{generatedRecipe.nutrition.per_serving.calories}</div>
                        <div className="text-xs text-gray-600 mt-1">Calorias</div>
                      </div>
                      {generatedRecipe.nutrition.per_serving.protein && (
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-700">{generatedRecipe.nutrition.per_serving.protein}</div>
                          <div className="text-xs text-gray-600 mt-1">Proteína</div>
                        </div>
                      )}
                      {generatedRecipe.nutrition.per_serving.carbs && (
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-700">{generatedRecipe.nutrition.per_serving.carbs}</div>
                          <div className="text-xs text-gray-600 mt-1">Carboidratos</div>
                        </div>
                      )}
                      {generatedRecipe.nutrition.per_serving.fat && (
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-700">{generatedRecipe.nutrition.per_serving.fat}</div>
                          <div className="text-xs text-gray-600 mt-1">Gordura</div>
                        </div>
                      )}
                    </div>
                    {generatedRecipe.nutrition.total?.calories && (
                      <div className="mt-3 pt-3 border-t border-green-300 text-center text-sm">
                        <span className="text-gray-600">Total da receita: </span>
                        <span className="font-bold text-green-800">{generatedRecipe.nutrition.total.calories} calorias</span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Instruções:</h4>
                  <ol className="space-y-1 text-sm list-decimal list-inside">
                    {generatedRecipe.instructions.map((instruction, index) => (
                      <li key={index} className="text-gray-700">{instruction}</li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
      
      {/* Limit Reached Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type={limitType}
      />
    </div>
  );
};

export default AIGeneratorPage;